import type { useTranslate } from 'react-polyglot';

import type { BaseWidgetItemData } from '@genially/document-model';
import { isInteractiveQuestionItemData } from '@genially/ui/build/components/SidebarSummary/utils/isInteractiveQuestion';
import {
  getWidgetQuestionText,
  getWidgetTitleText,
} from '@genially/ui/build/utils/analytics/getWidgetQuestionText';
import { removeRemirrorHtmlTags } from '@genially/ui/build/utils/analytics/processQuestions';
import { getSavedLanguage } from '@genially/ui/build/utils/language';

import type { PlayPostMessageInteractiveQuestion } from '../../../../../../../shared/domain/types/PlayPostMessageInteractiveQuestion';

/**
 * The statement a host sees for a resolved question, whichever widget type it came from.
 * A random element only knows it is random: the statement that was drawn lives in the
 * question bank, which this panel deliberately does not fetch.
 */
export const getQuestionText = (
  question: PlayPostMessageInteractiveQuestion,
  isRandom: boolean,
  t: ReturnType<typeof useTranslate>,
): string => {
  if (isRandom) {
    return t('__new.play.analyticsResults.question.random');
  }

  if (isInteractiveQuestionItemData(question)) {
    // The statement is rich text: the panel shows it as plain text, tags and all stripped.
    return (
      removeRemirrorHtmlTags((question as unknown as { Question?: string }).Question) ||
      t('__new.play.analyticsResults.question.untitled')
    );
  }

  const widget = question as BaseWidgetItemData;
  const widgetTitle = getWidgetTitleText(widget.script?.title, getSavedLanguage(), t);

  return (
    getWidgetQuestionText(widget, widgetTitle) ||
    t('__new.play.analyticsResults.question.untitled')
  );
};
